import { Stack, SxProps, TextField, Theme } from '@mui/material'
import { Controller, FieldPath, FieldValues } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'

import Tooltip from 'components/Tooltip'
import { inputWidth } from 'providers/material-ui/theme/constants'
import { createHelperText } from '../TextField'
import { DECIMAL_SEPARATOR, THOUSAND_SEPARATOR } from './constants'
import { NumberFieldProps } from './types'

/**
 * Optional field:
 * - use `.nullish()` instead of `.optional()` in Zod schema
 * - Zod resolver converts `undefined` to `0`, breaking input clearing
 */
export const NumberField = <T extends FieldValues>({
  control,
  disabled,
  fixedHelperText,
  helperText,
  label,
  name,
  sx,
  tooltip: tooltipProps,
  useTooltipForErrors,
  width,
}: NumberFieldProps<T>): React.JSX.Element => {
  const calculatedWidth: number | string = inputWidth[width ?? 'sm']

  const containerSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
  }

  const inputSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
    width: calculatedWidth,
  }

  return (
    <Controller
      control={control}
      name={name as FieldPath<T>}
      render={({ field, fieldState }) => {
        const hasError: boolean = Boolean(fieldState.error)
        const errorMessage: string = fieldState.error?.message || ''

        return (
          <Stack
            direction='row'
            sx={containerSxProps}
          >
            <NumericFormat
              allowNegative={false}
              customInput={TextField}
              decimalSeparator={DECIMAL_SEPARATOR}
              disabled={disabled}
              error={hasError}
              helperText={createHelperText<T>({
                errorMessage,
                fixedHelperText,
                hasError,
                helperText,
                useTooltipForErrors,
              })}
              id={name}
              label={label}
              onValueChange={({ floatValue }) => void field.onChange(floatValue ?? null)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ ...inputSxProps, ...sx }}
              thousandSeparator={THOUSAND_SEPARATOR}
              type='text'
              value={field.value ?? ''}
              valueIsNumericString={false}
            />

            {tooltipProps && <Tooltip {...tooltipProps} />}

            {hasError && useTooltipForErrors && (
              <Tooltip
                content={errorMessage}
                variant='error'
              />
            )}
          </Stack>
        )
      }}
    />
  )
}
